import { RegisterSchema } from "@gosaas/schemas";
import type { ConstantsOAuthStruct } from "@gosaas/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { AuthOauthSection } from "@/components/auth/auth-oauth-section";
import { AuthPage } from "@/components/auth/auth-page";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import {
	isAuthOAuthGitHubEnabled,
	isAuthOAuthGoogleEnabled,
} from "@/lib/modules";
import { showAnyAuthOAuthProvider } from "@/routes/auth/login";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import {
	getLastSignInMethod,
	type LastSignInMethod,
	setLastOauthProvider,
	setLastSignInMethod,
} from "@/utils/local-storage";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/register")({
	component: Register,
	head: () =>
		seo({
			title: "Register",
			description: "Register for an account",
			keywords: "register, account, create, sign up",
		}),
});

export function Register() {
	const router = useRouter();
	const [lastSignInMethod, setLastSignInMethodState] =
		useState<LastSignInMethod | null>(() => getLastSignInMethod());

	const showAnyOAuthProvider = showAnyAuthOAuthProvider();

	const form = useForm<z.infer<typeof RegisterSchema>>({
		resolver: zodResolver(RegisterSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			confirmPassword: "",
		},
	});

	const fetchGithubOauth = async () => {
		if (!isAuthOAuthGitHubEnabled()) {
			toast.error("Couldn't sign in with GitHub", {
				description: "This sign-in method is not enabled.",
			});
			return;
		}
		try {
			const response = await requestWithHandling<ConstantsOAuthStruct>(() =>
				apiClient.api.oauthGithubList(),
			);
			if (response.data?.url) {
				setLastOauthProvider("github");
				setLastSignInMethodState("github");
				window.location.href = response.data.url;
			} else {
				toast.error("Couldn't sign in with GitHub", {
					description: "Received an invalid response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't sign in with GitHub", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const fetchGoogleOauth = async () => {
		if (!isAuthOAuthGoogleEnabled()) {
			toast.error("Couldn't sign in with Google", {
				description: "This sign-in method is not enabled.",
			});
			return;
		}
		try {
			const response = await requestWithHandling<ConstantsOAuthStruct>(() =>
				apiClient.api.oauthGoogleList(),
			);
			if (response.data?.url) {
				setLastOauthProvider("google");
				setLastSignInMethodState("google");
				window.location.href = response.data.url;
			} else {
				toast.error("Couldn't sign in with Google", {
					description: "Received an invalid response from the server.",
				});
			}
		} catch (error) {
			toast.error("Couldn't sign in with Google", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const onSubmit = async (data: z.infer<typeof RegisterSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.authRegisterCreate({
					email: data.email,
					password: data.password,
					first_name: data.firstName,
					last_name: data.lastName,
				}),
			);
			toast.success("Account created successfully", {
				description: "Please check your email for verification instructions.",
			});
			setLastSignInMethod("email");

			router.navigate({
				to: "/auth/verify-email",
				search: {
					email: data.email,
				} as const,
			});
		} catch (error) {
			toast.error("Couldn't create your account", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Create your account"
			description={
				showAnyOAuthProvider
					? "Start with email or sign up with a provider."
					: "Use your email and a password to get started."
			}
			variant="comfortable"
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="firstName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>First name</FormLabel>
									<FormControl>
										<Input {...field} type="text" autoComplete="given-name" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="lastName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Last name</FormLabel>
									<FormControl>
										<Input {...field} type="text" autoComplete="family-name" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="email"
										required
										autoComplete="email"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Password</FormLabel>
									<FormControl>
										<PasswordInput
											{...field}
											required
											autoComplete="new-password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="confirmPassword"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Confirm password</FormLabel>
									<FormControl>
										<PasswordInput
											{...field}
											required
											autoComplete="new-password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<Button type="submit" className="h-11 w-full cursor-pointer">
						Create account
					</Button>
				</form>
			</Form>

			<AuthOauthSection
				lastSignInMethod={lastSignInMethod}
				onGoogle={fetchGoogleOauth}
				onGithub={fetchGithubOauth}
			/>

			<p className="mt-8 text-center text-base text-muted-foreground">
				Already registered?{" "}
				<Link
					to="/auth/login"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Sign in
				</Link>
			</p>
		</AuthPage>
	);
}
